import React, { useState, useRef } from 'react';

import { Formik, Form } from 'formik';
import ReCAPTCHA from 'react-google-recaptcha';

import Button from '@components/common/Button/Button';
import Input from '@components/common/Formik/FormikInputField';
import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import NotificationBloc from '@components/common/NotificationBloc/NotificationBloc';
import Text from '@components/common/Text/Text';
import AuthenticationPageContext from '@components/lpc/Authentication/AuthenticationPageContext';
import Auth from '@services/domain/Lpc/AuthService';
import { passwordForgottenLinkGeneration } from '@validations/userSchema';

import css from './LostPassword.module.scss';

type EmailFromProps = {
    setError: any;
    fromDashboard?: boolean;
    confirmSentEmailPopup?: (email: string) => void;
};

const EmailForm = ({ setError, fromDashboard, confirmSentEmailPopup }: EmailFromProps) => {
    const { t } = useTranslateLpc('lpc-authentication');
    const lastSubmittedValue = useRef('');
    const [isLoading, setIsLoading] = useState(false);
    const recaptchaRef = useRef<ReCAPTCHA>(null);

    const getInitialValues = () => {
        return {
            email: '',
        };
    };

    return (
        <AuthenticationPageContext.Consumer>
            {({ setPage, pages, setEmail }) => (
                <Formik
                    validateOnBlur={false}
                    validationSchema={passwordForgottenLinkGeneration}
                    initialValues={getInitialValues()}
                    onSubmit={async (values) => {
                        setIsLoading(true);
                        const captcha = await recaptchaRef.current?.executeAsync();
                        Auth.forgottenPassword({ captcha, ...values })
                            .then(() => {
                                fromDashboard
                                    ? confirmSentEmailPopup(values.email)
                                    : setPage(pages.emailSentConfirmation);
                                !fromDashboard && setEmail(values.email);
                            })
                            .catch(() => setError(true))
                            .finally(() => {
                                setIsLoading(false);
                                recaptchaRef.current?.reset();
                            });
                    }}
                >
                    {({ isSubmitting, values }) => {
                        if (!isSubmitting && lastSubmittedValue.current !== values.email) {
                            setError(false);
                            lastSubmittedValue.current = values.email;
                        }
                        return (
                            <Form className={css.form}>
                                {values?.email && (
                                    <ReCAPTCHA
                                        sitekey={process.env.RECAPTCHA_SITE_KEY}
                                        ref={recaptchaRef}
                                        size="invisible"
                                    />
                                )}
                                <div className={css.input}>
                                    <Input isFloatingLabel name="email" type="email" label={t('lost-password.email')} />
                                </div>

                                <div className={css.actions}>
                                    <Button
                                        width={'100%'}
                                        type={'submit'}
                                        size="large"
                                        variant="primary"
                                        isLoading={isLoading}
                                        isDisabled={isLoading}
                                    >
                                        {t('lost-password.send')}
                                    </Button>
                                    {!fromDashboard && (
                                        <Button
                                            customClass={css.actions__back}
                                            type={'button'}
                                            variant="tertiary"
                                            onClick={() => setPage(pages.login)}
                                        >
                                            {t('lost-password.back')}
                                        </Button>
                                    )}
                                </div>
                            </Form>
                        );
                    }}
                </Formik>
            )}
        </AuthenticationPageContext.Consumer>
    );
};

type LostPasswordProps = {
    fromDashboard?: boolean;
    confirmSentEmailPopup?: (email: string) => void;
};

const LostPassword = ({ fromDashboard, confirmSentEmailPopup }: LostPasswordProps) => {
    const [error, setError] = useState(false);

    const { t } = useTranslateLpc('lpc-authentication');
    return (
        <div className={fromDashboard ? `${css.custom_lost_password__wrapper} ` : ` ${css.lost_password__wrapper}`}>
            <h3>{t('lost-password.title')}</h3>
            <div className={css.details}>
                <Text variant="body_02" color="ui-secondary">
                    {t('lost-password.details')}
                </Text>
            </div>

            {error && (
                <NotificationBloc
                    customClass={css.notification}
                    onResetVisibility={(value) => setError(value)}
                    variant={'warning'}
                    message={t('lost-password.email-not-found')}
                />
            )}

            <EmailForm
                setError={setError}
                fromDashboard={fromDashboard}
                confirmSentEmailPopup={(email) => confirmSentEmailPopup(email)}
            />
        </div>
    );
};

export default LostPassword;
